import React from "react";

const monthKeys = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const formatAmount = (value: number) =>
  new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 0,
  }).format(value || 0);

const getRowTotal = (row: any) =>
  monthKeys.reduce((sum, month) => sum + Number(row?.[month] || 0), 0);

const CustomerSalesAnalysisTable = ({ data = [] }: { data?: any[] }) => {
  const rows = (data || []).map((row) => ({
    ...row,
    total: getRowTotal(row),
  }));

  const monthlyTotals = monthKeys.reduce((acc: Record<string, number>, month) => {
    acc[month] = rows.reduce((sum, row) => sum + Number(row?.[month] || 0), 0);
    return acc;
  }, {});

  const grandTotal = rows.reduce((sum, row) => sum + row.total, 0);
  const totalFrequency = rows.reduce(
    (sum, row) => sum + Number(row?.Frequency || 0),
    0
  );
  const activeCustomers = rows.filter((row) => row.total > 0).length;
  const strongestMonth = monthKeys.reduce((best, month) =>
    monthlyTotals[month] > monthlyTotals[best] ? month : best
  , monthKeys[0]);

  if (!rows.length) {
    return (
      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-500">
        No customer sales analysis data found for the selected period.
      </div>
    );
  }

  return (
    <div className="mt-6 space-y-5">
      <div className="grid gap-4 md:grid-cols-4">
        <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-blue-700">
            Customers
          </div>
          <div className="mt-2 text-2xl font-bold text-slate-900">{rows.length}</div>
          <div className="mt-1 text-sm text-slate-500">
            {activeCustomers} with sales
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Frequency
          </div>
          <div className="mt-2 text-2xl font-bold text-slate-900">
            {formatAmount(totalFrequency)}
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Grand Total
          </div>
          <div className="mt-2 text-2xl font-bold text-slate-900">
            {formatAmount(grandTotal)}
          </div>
        </div>
        <div className="rounded-2xl border border-amber-100 bg-amber-50 p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-amber-700">
            Peak Month
          </div>
          <div className="mt-2 text-2xl font-bold text-slate-900">
            {strongestMonth}
          </div>
          <div className="mt-1 text-sm text-slate-500">
            {formatAmount(monthlyTotals[strongestMonth])}
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="border-b border-slate-200 bg-slate-50 px-5 py-4">
          <div className="text-lg font-semibold text-slate-900">
            Customer Sales Breakdown
          </div>
          <div className="mt-1 text-sm text-slate-500">
            Monthly sales by customer and business unit.
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-[1600px] w-full text-sm">
            <thead className="bg-slate-100 text-slate-600">
              <tr>
                <th className="sticky left-0 z-20 border-b border-slate-200 bg-slate-100 px-4 py-3 text-left font-semibold">
                  Customer
                </th>
                <th className="sticky left-[240px] z-20 border-b border-slate-200 bg-slate-100 px-4 py-3 text-left font-semibold">
                  Business Unit
                </th>
                <th className="border-b border-slate-200 px-4 py-3 text-right font-semibold">
                  Frequency
                </th>
                {monthKeys.map((month) => (
                  <th
                    key={month}
                    className="border-b border-slate-200 px-4 py-3 text-right font-semibold whitespace-nowrap"
                  >
                    {month}
                  </th>
                ))}
                <th className="border-b border-slate-200 px-4 py-3 text-right font-semibold">
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr
                  key={`${row.customer_name}-${row.business_unit_name}-${index}`}
                  className="border-b border-slate-100 hover:bg-slate-50"
                >
                  <td className="sticky left-0 z-10 min-w-[240px] border-r border-slate-100 bg-white px-4 py-3 font-medium text-slate-900">
                    {row.customer_name || "-"}
                  </td>
                  <td className="sticky left-[240px] z-10 min-w-[220px] border-r border-slate-100 bg-white px-4 py-3 text-slate-700">
                    {row.business_unit_name || "-"}
                  </td>
                  <td className="px-4 py-3 text-right text-slate-700">
                    {formatAmount(Number(row.Frequency || 0))}
                  </td>
                  {monthKeys.map((month) => {
                    const value = Number(row?.[month] || 0);
                    return (
                      <td
                        key={`${row.customer_name}-${month}`}
                        className={`px-4 py-3 text-right ${
                          value > 0 ? "font-semibold text-slate-900" : "text-slate-400"
                        }`}
                      >
                        {formatAmount(value)}
                      </td>
                    );
                  })}
                  <td className="bg-slate-50 px-4 py-3 text-right font-bold text-slate-900">
                    {formatAmount(row.total)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-slate-100 font-semibold text-slate-900">
                <td
                  className="sticky left-0 z-20 border-t border-slate-200 bg-slate-100 px-4 py-4"
                  colSpan={2}
                >
                  Monthly Totals
                </td>
                <td className="border-t border-slate-200 px-4 py-4 text-right">
                  {formatAmount(totalFrequency)}
                </td>
                {monthKeys.map((month) => (
                  <td
                    key={`total-${month}`}
                    className="border-t border-slate-200 px-4 py-4 text-right"
                  >
                    {formatAmount(monthlyTotals[month])}
                  </td>
                ))}
                <td className="border-t border-slate-200 bg-slate-200 px-4 py-4 text-right">
                  {formatAmount(grandTotal)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CustomerSalesAnalysisTable;
